import { useEffect, useState, ChangeEvent } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";

import { environment } from "../../config";
import useHttp from "../../hook/useHttp";
import {
  SettingEvent,
  SettingEventsResponse,
  BdsEvent,
  ExternalEvent,
  PortalType,
  PORTAL_LABELS,
  MANUAL_PORTALS,
  SettingPortalResponse,
} from "../../models/Settings";
import Spinner from "../UI/Spinner";
import Table from "../UI/Table";

const SettingsEventComponent = () => {
  const { isLoading, error, sendRequest: fetchSettingEvents } = useHttp();
  const {
    isLoading: isLoadingSave,
    error: errorSave,
    sendRequest: saveSettingEvents,
  } = useHttp();
  const {
    isLoading: isLoadingPortal,
    sendRequest: fetchPortalStatus,
  } = useHttp();
  const {
    isLoading: isLoadingPortalSave,
    sendRequest: savePortalStatus,
  } = useHttp();
  const [selectedPortal, setSelectedPortal] = useState<PortalType | "">("");
  const [settingEvents, setSettingEvents] = useState<SettingEvent[]>([]);
  const [bdsEvents, setBdsEvents] = useState<BdsEvent[]>([]);
  const [externalEvents, setExternalEvents] = useState<ExternalEvent[]>([]);
  const [isPortalActive, setIsPortalActive] = useState<boolean>(true);

  const { register, control, handleSubmit, reset, setValue, watch } =
    useForm({
      defaultValues: {
        events: [] as SettingEvent[],
      },
    });
  const watchedEvents = watch("events");
  const { fields, prepend, remove } = useFieldArray({
    control,
    name: "events",
  });

  const isManualPortal = selectedPortal && MANUAL_PORTALS.includes(selectedPortal);

  const handleAddRow = () => {
    if (!selectedPortal) return;
    prepend({
      portal: selectedPortal,
      idEvent: "",
      idProdottoBds: null,
      idPartenzaBds: null,
      idPartenzaEvent: null,
      isActive: true,
    });
  };

  const handleDeleteRow = (index: number) => {
    remove(index);
  };

  useEffect(() => {
    if (selectedPortal) {
      fetchSettingEvents(
        {
          url: `${environment.API_ENDPOINT}/setting/events?portal=${selectedPortal}`,
        },
        (response: SettingEventsResponse) => {
          const isManual = MANUAL_PORTALS.includes(selectedPortal);
          if (isManual) {
            const normalizedEvents = response.settingsEvent.map((event) => ({
              ...event,
              idProdottoBds: event.idProdottoBds ?? null,
              idPartenzaBds: event.idPartenzaBds ?? null,
              idPartenzaEvent: event.idPartenzaEvent ?? null,
            }));
            setSettingEvents(normalizedEvents);
            reset({ events: normalizedEvents });
            setExternalEvents([]);
          } else {
            const events = response.externalEvents.map((event) => {
              const setting = response.settingsEvent.find(
                (item) => item.idEvent === event.id
              );
              if (setting) {
                return {
                  ...setting,
                  idProdottoBds: setting.idProdottoBds ?? null,
                  idPartenzaBds: setting.idPartenzaBds ?? null,
                  isEdit: false,
                };
              }
              return {
                portal: event.portal,
                idEvent: event.id,
                idPartenzaBds: null,
                idProdottoBds: null,
                idPartenzaEvent: null,
                isActive: true,
                isEdit: false,
              };
            }) as SettingEvent[];
            setSettingEvents(events);
            reset({ events: events });
            setExternalEvents(response.externalEvents);
          }
          setBdsEvents(response.bdsEvents);
        }
      );
    }
  }, [selectedPortal, fetchSettingEvents, reset]);

  useEffect(() => {
    reset({ events: settingEvents });
  }, [reset, settingEvents]);

  const onSaveSettingEvents = (settingEventsForm: SettingEvent[]) => {
    saveSettingEvents(
      {
        url: `${environment.API_ENDPOINT}/setting/events`,
        body: {
          settingEvents: settingEventsForm.map((event) => ({
            portal: selectedPortal,
            idEvent: event.idEvent,
            idProdottoBds: event.idProdottoBds,
            idPartenzaBds: event.idPartenzaBds,
            idPartenzaEvent: event.idPartenzaEvent || null,
            isActive: event.isActive !== false,
          })),
        },
        method: "PUT",
      },
      (response: { settingsEvent: SettingEvent[] }) => {
        setSettingEvents(response.settingsEvent);
        alert("Salvato con successo!");
      }
    );
  };

  const handleProdottoChange = (
    index: number,
    event: ChangeEvent<HTMLSelectElement>
  ) => {
    const newValue = event.target?.value ? +event.target.value : null;
    setValue(`events.${index}.idProdottoBds`, newValue);
    setValue(`events.${index}.idPartenzaBds`, null);
  };

  const handlePartenzaChange = (
    index: number,
    event: ChangeEvent<HTMLSelectElement>
  ) => {
    const newValue = event.target?.value ? +event.target.value : null;
    setValue(`events.${index}.idPartenzaBds`, newValue);
  };

  const handlePortalChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedPortal(event.target.value as PortalType);
  };

  useEffect(() => {
    if (selectedPortal) {
      fetchPortalStatus(
        {
          url: `${environment.API_ENDPOINT}/setting/portal/${selectedPortal}`,
        },
        (response: SettingPortalResponse) => {
          setIsPortalActive(response.isActive);
        }
      );
    }
  }, [selectedPortal, fetchPortalStatus]);

  const handlePortalActiveToggle = () => {
    if (!selectedPortal) return;
    const newStatus = !isPortalActive;
    savePortalStatus(
      {
        url: `${environment.API_ENDPOINT}/setting/portal`,
        body: {
          portal: selectedPortal,
          isActive: newStatus,
        },
        method: "PUT",
      },
      () => {
        setIsPortalActive(newStatus);
      }
    );
  };

  return (
    <div className="py-5">
      <div className="mb-4 d-flex align-items-center gap-4">
        <div>
          <label htmlFor="portal-select" className="form-label me-3">
            Seleziona Portale:
          </label>
          <select
            id="portal-select"
            className="form-select d-inline-block"
            style={{ width: "auto" }}
            value={selectedPortal}
            onChange={handlePortalChange}
          >
            <option value="">-- Seleziona un portale --</option>
            {Object.entries(PORTAL_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        {selectedPortal && (
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="portal-active-switch"
              checked={isPortalActive}
              onChange={handlePortalActiveToggle}
              disabled={isLoadingPortal || isLoadingPortalSave}
            />
            <label className="form-check-label" htmlFor="portal-active-switch">
              {isPortalActive ? "Portale Attivo" : "Portale Disattivato"}
            </label>
          </div>
        )}
      </div>

      {(error || errorSave) && (
        <div className="alert alert-danger">
          Si è verificato un problema.
        </div>
      )}

      {!error && !errorSave && (
        <Spinner showSpinner={isLoading || isLoadingSave}>
          {selectedPortal && (
            <form
              onSubmit={handleSubmit((data) =>
                onSaveSettingEvents(
                  data.events.map((event) => {
                    event.idPartenzaBds = event.idPartenzaBds
                      ? +event.idPartenzaBds
                      : null;
                    event.idProdottoBds = event.idProdottoBds
                      ? +event.idProdottoBds
                      : null;
                    return event;
                  })
                )
              )}
            >
              <div className="mb-4 d-flex justify-content-between gap-2">
                <button title="Salva" className="btn btn-primary" type="submit">
                  <i className="bi-save me-2"></i>
                  Salva
                </button>
                {isManualPortal && (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleAddRow}
                  >
                    <i className="bi-plus-lg me-2"></i>
                    Aggiungi riga
                  </button>
                )}
              </div>
              <Table>
                <thead>
                  <tr>
                    <th style={{ width: isManualPortal ? "30%" : "40%" }}>
                      {isManualPortal ? "Pattern Prodotto" : "Prodotto Esterno"}
                    </th>
                    {isManualPortal && <th style={{ width: "15%" }}>Opzione/Partenza</th>}
                    <th>Prodotto BDS</th>
                    <th>Partenza BDS</th>
                    {isManualPortal && <th style={{ width: "80px" }}>Azioni</th>}
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, index) => {
                    const currentEvent = watchedEvents?.[index];
                    const prodotto = bdsEvents.find(
                      (event) => +event.id === +(currentEvent?.idProdottoBds || -1)
                    );
                    const externalEvent = externalEvents.find(
                      (event) =>
                        event.id === field.idEvent
                    );

                    return (
                      <tr key={field.id || index}>
                        <td>
                          {isManualPortal ? (
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Es: Tour Burano"
                              {...register(`events.${index}.idEvent`)}
                            />
                          ) : (
                            externalEvent?.name || field.idEvent
                          )}
                        </td>
                        {isManualPortal && (
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Es: ore 14:30"
                              title="Valore per differenziare le partenze (opzionale)"
                              {...register(`events.${index}.idPartenzaEvent`)}
                            />
                          </td>
                        )}
                        <td>
                          <Controller
                            name={`events.${index}.idProdottoBds`}
                            control={control}
                            render={({ field: controllerField }) => (
                              <select
                                className="form-select"
                                {...controllerField}
                                value={controllerField.value?.toString() || ""}
                                onChange={(e) => {
                                  const value = e.target.value ? Number(e.target.value) : null;
                                  controllerField.onChange(value);
                                  handleProdottoChange(index, e);
                                }}
                                required
                              >
                                <option value=""></option>
                                {bdsEvents.map((event) => (
                                  <option key={event.id} value={event.id}>
                                    <div dangerouslySetInnerHTML={{ __html: event.name}} />
                                  </option>
                                ))}
                              </select>
                            )}
                          />
                        </td>
                        <td>
                          <Controller
                            name={`events.${index}.idPartenzaBds`}
                            control={control}
                            render={({ field: controllerField }) => (
                              <select
                                className="form-select"
                                {...controllerField}
                                value={controllerField.value?.toString() || ""}
                                onChange={(e) => {
                                  const value = e.target.value ? Number(e.target.value) : null;
                                  controllerField.onChange(value);
                                  handlePartenzaChange(index, e);
                                }}
                                required
                              >
                                <option value=""></option>
                                {bdsEvents
                                  .find(
                                    (event) =>
                                      event.id === (prodotto?.id ?? -1)
                                  )
                                  ?.partenze.map((event) => {
                                    return (
                                      <option key={event.id} value={event.id}>
                                        {event.name}
                                      </option>
                                    );
                                  })}
                              </select>
                            )}
                          />
                        </td>
                        {isManualPortal && (
                          <td>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDeleteRow(index)}
                              title="Elimina"
                            >
                              <i className="bi-trash"></i>
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </form>
          )}
        </Spinner>
      )}
    </div>
  );
};

export default SettingsEventComponent;
